/**
 * Tarayıcıda PDF metin katmanı okuma (pdf.js; yapay zekâ yok, dosya sunucuya gitmez). Her sayfanın metin parçaları
 * konumlarına göre satırlara dizilir (tablo satırları tek satır olur, hücreler arası boşlukla). Taranmış (metinsiz) PDF'te
 * satır çıkmaz → `scanned`.
 */
export interface PdfItem { str: string; x: number; y: number; w: number; h: number }
export interface PdfPageText { page: number; lines: string[] }
export interface PdfText { pages: PdfPageText[]; pageCount: number; chars: number; scanned: boolean }

/** Parçaları satırlara dizer: y'ye göre (PDF'te yukarıdan aşağı y azalır) gruplar, satır içinde x'e göre sıralar. */
export function itemsToLines(items: PdfItem[]): string[] {
  const it = items.filter((i) => i.str.trim() !== "");   // pdf.js boşluk parçaları atılır; boşluk konumdan çıkarılır
  if (!it.length) return [];
  const hs = it.map((i) => i.h).filter((h) => h > 0).sort((a, b) => a - b);
  const hMed = hs.length ? hs[Math.floor(hs.length / 2)]! : 10;
  // Satır = düşey aralığı örtüşen parçalar (y taban çizgisi, h yükseklik). Uzun parçalar satırı kurar, alt/üst simgeler
  // (γn, m³) örtüşme ≥ %50 ile ana satıra katılır.
  const rows: { lo: number; hi: number; items: PdfItem[] }[] = [];
  for (const i of [...it].sort((a, b) => (b.h || hMed) - (a.h || hMed) || b.y - a.y)) {
    const h = i.h || hMed, lo = i.y, hi = i.y + h;
    const row = rows.find((r) => Math.min(hi, r.hi) - Math.max(lo, r.lo) >= 0.5 * Math.min(h, r.hi - r.lo));
    if (row) row.items.push(i); else rows.push({ lo, hi, items: [i] });
  }
  rows.sort((a, b) => b.lo - a.lo);
  return rows.map((r) => {
    const s = r.items.sort((a, b) => a.x - b.x);
    let out = "", end = -Infinity;
    for (const i of s) {
      const gap = i.x - end, h = i.h || hMed;
      if (out && gap > h * 1.5) out += "  ";        // geniş boşluk: tablo hücresi
      else if (out && gap > h * 0.18 && !out.endsWith(" ") && !i.str.startsWith(" ")) out += " ";
      out += i.str;
      end = Math.max(end, i.x + i.w);
    }
    return out.replace(/\s+$/, "");
  }).filter((l) => l.trim() !== "");
}

/** PDF'i okur (en çok `maxPages` sayfa). pdf.js tembel yüklenir (ana pakete girmez). */
export async function readPdfText(data: ArrayBuffer, maxPages = 80): Promise<PdfText> {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const w = await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url");
    pdfjs.GlobalWorkerOptions.workerSrc = w.default;
  }
  const task = pdfjs.getDocument({ data: new Uint8Array(data), enableXfa: false });
  const doc = await task.promise;
  try {
    const pages: PdfPageText[] = [];
    let chars = 0;
    const n = Math.min(doc.numPages, maxPages);
    for (let p = 1; p <= n; p++) {
      const page = await doc.getPage(p);
      const tc = await page.getTextContent();
      const items: PdfItem[] = [];
      for (const raw of tc.items) {
        if (!("str" in raw)) continue;
        const tr = raw.transform as number[];
        items.push({ str: raw.str, x: tr[4] ?? 0, y: tr[5] ?? 0, w: raw.width, h: raw.height || Math.abs(tr[3] ?? 0) });
        chars += raw.str.trim().length;
      }
      pages.push({ page: p, lines: itemsToLines(items) });
      page.cleanup();
    }
    return { pages, pageCount: doc.numPages, chars, scanned: chars < Math.max(40, n * 25) };
  } finally { void task.destroy(); }
}
